if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          pagination: this.querySelector('.swiper-pagination'),
        };
        this.mql = window.matchMedia('(min-width: 768px)');
        this.thumbSwiper = null;
        this.mainSwiper = null;
        this.isNativeScroll = !this.mql.matches;
        this.classList.toggle('media-gallery--native', this.isNativeScroll);
        this._activeIndex = 0;

        // Wait briefly for Swiper to be available (since it's loaded with defer)
        if (this.isNativeScroll) {
          this.initNativeGallery();
        } else {
          const checkSwiper = setInterval(() => {
            if (typeof Swiper !== 'undefined') {
              clearInterval(checkSwiper);
              this.initSwiper();
            }
          }, 100);
        }

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initNativeGallery() {
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', () => {
                this.setActiveMedia(mediaToSwitch.dataset.target, false);
              });
          });
          this.playThumbnailVideos();
        }

        this.initVideoClickToPause();
        this.playAllSlideVideos();
        this.elements.viewer.classList.add('swiper-vertical');
        const pagination = this.elements.pagination;
        if (pagination) {
          pagination.classList.add('swiper-pagination-clickable');
          const slideCount = this.elements.viewer.querySelectorAll('.swiper-slide').length;
          pagination.innerHTML = '';
          for (let i = 0; i < slideCount; i++) {
            const bullet = document.createElement('span');
            bullet.className = 'swiper-pagination-bullet';
            bullet.addEventListener('click', () => this._scrollViewerToIndex(i));
            pagination.appendChild(bullet);
          }
        }
        this._onActiveSlideChange(0);
        window.__pdpGallerySlidePrev = () => {
          if (this._activeIndex <= 0) return false;
          this._scrollViewerToIndex(this._activeIndex - 1);
          return true;
        };
        const SWIPE_UP_EXPAND_THRESHOLD_PX = 40;
        const isAtGalleryVisualEnd = () => {
          const slides = this.elements.viewer.querySelectorAll('.swiper-slide');
          const lastSlide = slides[slides.length - 1];
          if (!lastSlide) return false;
          const viewerRect = this.elements.viewer.getBoundingClientRect();
          return lastSlide.getBoundingClientRect().bottom <= viewerRect.bottom + 2;
        };
        let galleryTouchStartY = null;
        let galleryTouchStartedAtEnd = false;
        this.elements.viewer.addEventListener('touchstart', (e) => {
          galleryTouchStartY = e.touches[0] ? e.touches[0].clientY : null;
          galleryTouchStartedAtEnd = isAtGalleryVisualEnd();
        }, { passive: true });
        this.elements.viewer.addEventListener('touchend', (e) => {
          const startY = galleryTouchStartY;
          const startedAtEnd = galleryTouchStartedAtEnd;
          galleryTouchStartY = null;
          if (startY === null || !startedAtEnd) return;
          if (!isAtGalleryVisualEnd()) return;
          const touch = e.changedTouches && e.changedTouches[0];
          if (!touch) return;
          const deltaY = startY - touch.clientY; // positive = swiped up
          if (deltaY < SWIPE_UP_EXPAND_THRESHOLD_PX) return;
          if (typeof window.__pdpExpandPanel === 'function') window.__pdpExpandPanel();
        }, { passive: true });

        const viewer = this.elements.viewer;

        // A finger placed on the gallery to arrest an in-flight momentum/glide scroll
        // reads to the browser as a tap (or a short drag that doesn't move the page),
        // which still synthesizes a click — landing on the active slide's zoom-toggle
        // (modal-opener) and popping the zoom modal open instead of just stopping the
        // scroll. The decisive signal is whether the gallery was actively scrolling the
        // instant the finger landed (checked at touchstart, not touchend — the finger
        // can rest on the screen for a beat before lifting, so timing the check at
        // touchend against a short window can miss a touchstart that legitimately
        // interrupted live momentum). Any click that follows such a touch is swallowed,
        // regardless of how far the finger moved in between — a real drag far enough to
        // count as a swipe never reaches the browser's own click synthesis anyway.
        const TAP_STOP_SCROLL_WINDOW_MS = 150;
        let lastScrollEventTime = 0;
        let suppressNextClick = false;
        viewer.addEventListener('scroll', () => {
          lastScrollEventTime = performance.now();
        }, { passive: true });
        viewer.addEventListener('touchstart', () => {
          if (performance.now() - lastScrollEventTime < TAP_STOP_SCROLL_WINDOW_MS) {
            suppressNextClick = true;
          }
        }, { passive: true });
        viewer.addEventListener('click', (e) => {
          if (!suppressNextClick) return;
          suppressNextClick = false;
          e.preventDefault();
          e.stopPropagation();
        }, true);

        let scrollRaf = null;
        const handleScroll = () => {
          scrollRaf = null;
          const scrollHeight = viewer.scrollHeight - viewer.clientHeight;
          const progress = scrollHeight > 0 ? Math.min(1, Math.max(0, viewer.scrollTop / scrollHeight)) : 0;
          this._nativeProgress = progress;
          this.updatePaginationIndicator();

          const slides = viewer.querySelectorAll('.swiper-slide');
          if (!slides.length) return;
          const viewerRect = viewer.getBoundingClientRect();
          const viewerCenter = viewerRect.top + viewer.clientHeight / 2;
          let closestIndex = 0;
          let closestDistance = Infinity;
          slides.forEach((slide, index) => {
            const rect = slide.getBoundingClientRect();
            const slideCenter = rect.top + rect.height / 2;
            const distance = Math.abs(slideCenter - viewerCenter);
            if (distance < closestDistance) {
              closestDistance = distance;
              closestIndex = index;
            }
          });
          if (closestIndex !== this._activeIndex) {
            this._activeIndex = closestIndex;
            this._onActiveSlideChange(closestIndex);
          }
        };
        viewer.addEventListener('scroll', () => {
          if (scrollRaf) return;
          scrollRaf = requestAnimationFrame(handleScroll);
        }, { passive: true });
        // This hand-rolled flick-glide exists only to compensate for iOS's own
        // inertial coast being too short (see media-gallery.js history/memory).
        // Android's native fling/momentum scroll already covers several images
        // per flick on its own — running this JS-driven scrollTop glide (and
        // killNativeMomentum's overflow toggle) on top of it makes two separate
        // drivers fight for the same scrollTop, which reads as the gallery
        // jumping around and getting stuck right at the first/last image (same
        // "custom JS touch/scroll physics fights Android's native scroll" issue
        // already worked around elsewhere in this codebase — see
        // section-plp-empty.js and tabbed-related-products.js, both of which
        // skip Swiper's JS touch handling on Android for the same reason). So
        // this whole glide layer is iOS-only; Android gets pure native scroll.
        const isIOS = /iP(hone|od|ad)/.test(navigator.userAgent)
          || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

        if (isIOS) {
          const FLICK_MIN_VELOCITY = 0.18; // px/ms — below this, leave native momentum alone
          const GLIDE_PER_VELOCITY = 6; // viewport-heights of glide per px/ms of release velocity
          const GLIDE_BASE_DURATION_MS = 450; // travel time for a short, single-image glide
          const GLIDE_MS_PER_VIEWPORT = 220; // extra travel time per additional viewport-height of distance, so a longer glide takes proportionally longer instead of covering more ground at the same fixed duration (which is what read as "too fast")
          const GLIDE_MAX_DURATION_MS = 2600; // raised alongside GLIDE_PER_VELOCITY so a hard flick's longer distance doesn't get compressed back into the old cap, which would re-introduce a tail-end speed-up
          const VELOCITY_SAMPLE_WINDOW_MS = 80; // how far back to look for the release speed, so a brief pause right at the very end of a drag (common) doesn't get averaged into the flick reading

          let glideRaf = null;
          let scrollSamples = [];
          viewer.addEventListener('scroll', () => {
            scrollSamples.push({ top: viewer.scrollTop, t: performance.now() });
            if (scrollSamples.length > 12) scrollSamples.shift();
          }, { passive: true });
          viewer.addEventListener('touchstart', () => {
            scrollSamples = [];
            if (glideRaf) { cancelAnimationFrame(glideRaf); glideRaf = null; }
          }, { passive: true });
          const killNativeMomentum = () => {
            const prevOverflow = viewer.style.overflowY;
            viewer.style.overflowY = 'hidden';
            void viewer.offsetHeight;
            viewer.style.overflowY = prevOverflow;
          };

          const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
          const glideBy = (distance) => {
            if (glideRaf) cancelAnimationFrame(glideRaf);
            const maxScrollTop = viewer.scrollHeight - viewer.clientHeight;
            const startTop = viewer.scrollTop;
            const targetTop = Math.min(maxScrollTop, Math.max(0, startTop + distance));
            const delta = targetTop - startTop;
            if (Math.abs(delta) < 1) { glideRaf = null; return; }
            const viewportsCovered = Math.abs(delta) / viewer.clientHeight;
            const duration = Math.min(
              GLIDE_MAX_DURATION_MS,
              GLIDE_BASE_DURATION_MS + viewportsCovered * GLIDE_MS_PER_VIEWPORT
            );
            let startTime = null;
            const step = (now) => {
              if (startTime === null) startTime = now;
              const t = Math.min((now - startTime) / duration, 1);
              viewer.scrollTop = startTop + delta * easeOutCubic(t);
              glideRaf = t < 1 ? requestAnimationFrame(step) : null;
            };
            glideRaf = requestAnimationFrame(step);
          };

          viewer.addEventListener('touchend', () => {
            if (scrollSamples.length < 2) return;
            const newest = scrollSamples[scrollSamples.length - 1];
            let reference = newest;
            for (let i = scrollSamples.length - 2; i >= 0; i--) {
              if (newest.t - scrollSamples[i].t > VELOCITY_SAMPLE_WINDOW_MS) break;
              reference = scrollSamples[i];
            }
            const dt = newest.t - reference.t;
            if (dt <= 0) return;
            const velocity = (newest.top - reference.top) / dt; // positive = scrolling forward
            if (Math.abs(velocity) < FLICK_MIN_VELOCITY) return;
            killNativeMomentum();
            glideBy(velocity * GLIDE_PER_VELOCITY * viewer.clientHeight);
          }, { passive: true });
        }

        if (document.readyState === 'complete') {
          this.initScrollForMoreHint();
        } else {
          window.addEventListener('load', () => this.initScrollForMoreHint(), { once: true });
        }
      }
      _onActiveSlideChange(index) {
        const slides = this.elements.viewer.querySelectorAll('.swiper-slide');
        const activeSlide = slides[index];
        if (activeSlide) {
          this.playActiveMedia(activeSlide);
          this.playAllSlideVideos();
          if (this.elements.thumbnails) {
            const mediaId = activeSlide.dataset.mediaId;
            this.updateActiveThumbnail(mediaId);
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              this.announceLiveRegion(activeSlide, activeThumbnail.dataset.mediaPosition);
            }
          }
        }
        if (index >= slides.length - 1) {
          const hint = document.querySelector('.scroll-for-more-hint');
          if (hint) hint.style.display = 'none';
        }
        this.updatePaginationIndicator();
      }

      initSwiper() {
        if (this.elements.thumbnails) {
          this.thumbSwiper = new Swiper(this.elements.thumbnails, {
            direction: 'vertical',
            slidesPerView: 'auto',
            spaceBetween: 1,
            freeMode: {
              enabled: true,
              sticky: false,
              momentumRatio: 0.5,
              momentumVelocityRatio: 0.5,
            },
            mousewheel: {
              enabled: true,
              forceToAxis: true,
              releaseOnEdges: true,
              sensitivity: 1,
            },
            watchSlidesProgress: true,
            navigation: {
              nextEl: this.elements.thumbnails.querySelector('.slider-button--next'),
              prevEl: this.elements.thumbnails.querySelector('.slider-button--prev'),
            },
          });
          
          // Legacy thumbnail click support
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', () => {
                this.setActiveMedia(mediaToSwitch.dataset.target, false);
              });

            // Desktop hover preview — show hovered thumbnail's slide, stays active
            mediaToSwitch.addEventListener('mouseenter', () => {
              if (!this.mql.matches) return;
              this._suppressThumbSync = true;
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
            });
          });

          // Autoplay videos inside thumbnail strip
          this.playThumbnailVideos();
        }

        this.mainSwiper = new Swiper(this.elements.viewer, {
          direction: 'vertical',
          slidesPerView: 'auto',
          spaceBetween: 1,
          slidesOffsetAfter: this.mql.matches ? 16 : 0,
          speed: 600,
          grabCursor: true,
          // threshold delays tracking until the finger has moved this many px, then
          // jumps the slide by the whole missed distance at once — 0 tracks from the
          // very first pixel so there's no catch-up jump at the start of a swipe.
          threshold: 0,
          longSwipesRatio: 0.2,
          // Hard stop at the first/last slide — no stretch and no white gap beyond
          // the gallery bounds on real touch devices.
          resistanceRatio: 0,
          // observer/resizeObserver/observeParents are all OFF — same disease as the
          // observeParents fix below, just one level further in: `observer:true` watches
          // the swiper's OWN subtree (not just ancestors) for DOM/class changes and
          // force-recalculates (re-clamping translate to the nearest slide) on each one.
          // The wheel handler further down writes translate directly every tick and also
          // toggles classes inside that same subtree as a side effect (updateSlidesClasses,
          // video play/pause classes, the pagination indicator's animating class) — each of
          // those was re-triggering Swiper's own recalculation mid-gesture, racing our
          // manual translate write. Depending on timing that race either silently reset
          // translate back onto a slide boundary (read as the gallery freezing) or made a
          // mid-gallery tick compute a bogus edge overflow (read as the wheel scroll
          // leaking through to the body). `mainSwiper.update()` is now called manually at
          // the actual known reflow points instead (variant swap re-render, window resize).
          //
          // observeParents watches every ANCESTOR element for DOM/class changes and
          // force-recalculates on each one. The mobile parallax script toggles classes
          // (is-scrolling/is-dragging/is-snapping/etc.) on the gallery's parents dozens
          // of times per touch gesture — each toggle was triggering a mid-drag Swiper
          // recalculation, reading as a freeze/stutter while the finger was still
          // moving. The parallax script already calls swiper.update() manually at the
          // specific moments layout actually changes, so this auto-watching is redundant.
          observer: false,
          observeParents: false,
          resizeObserver: false,
          freeMode: {
            enabled: true,
            // True free scroll — rests wherever momentum runs out, never forced onto
            // a slide edge. sticky:true and a manual post-gesture snap were both tried
            // to avoid resting mid-picture, but both made every gesture feel like
            // paging (stop-per-image) instead of a continuous scroll.
            sticky: false,
            momentum: true,
            /* Native-like inertia on desktop trackpad/wheel: Swiper's default
               deceleration kills a flick after ~1 screen, reading as a forced stop
               mid-gallery. Higher ratios let one flick glide through the gallery
               like native scroll. Tuning history on mobile: 0.6/0.8 (too little —
               only 1-2 images/swipe) → 1/1 stock (still only ~1.5 images/swipe on
               real touch hardware) → 5/3 and 2/2 (a hard/fast flick reached the
               last slide almost instantly, skipping past intermediate images).
               Root cause (from Swiper's free-mode source): momentumVelocityRatio
               directly scales the velocity used ONLY when the glide hits a
               boundary — Swiper recalculates that final approach's duration as
               remainingDistance / effectiveVelocity, literal physics, so a bigger
               ratio makes a hard flick's arrival at the last image shrink toward
               zero. momentumRatio instead governs the FIXED ~1000*ratio ms glide
               window used for ordinary mid-gallery movement and does not affect
               boundary speed at all. Splitting the two: velocityRatio lowered to
               slow the final approach to the last image regardless of flick
               strength; momentumRatio raised to compensate so ordinary swipes in
               the middle of the gallery still cover several images. */
            momentumRatio: this.mql.matches ? 3 : 4,
            momentumVelocityRatio: this.mql.matches ? 2 : 0.8,
            momentumBounce: false,
          },
          // Swiper's native mousewheel module is intentionally NOT used —
          // its releaseOnEdges cooldown is private internal state with no
          // API to reset from outside. Once it releases at an edge to hand
          // off to page scroll, it only re-arms on its own mouseenter
          // listener — so a wheel gesture that changes direction back
          // toward the gallery without the cursor actually moving (e.g.
          // scroll down to the page bottom, then scroll back up with the
          // cursor sitting still over the gallery) can land with NEITHER
          // Swiper's module (still released) NOR the page (cursor is over
          // the gallery, not the page) handling the wheel — the gallery
          // reads as completely frozen until a real mousemove fires a fresh
          // mouseenter. The wheel listener below drives the gallery's
          // translate directly instead, so there is no separate internal
          // module state that can ever get out of sync with it.
          mousewheel: false,
          pagination: {
            el: this.elements.pagination,
            clickable: true,
          },
          // Prevent Swiper from releasing the touch at the last edge on narrow
          // touch devices, which can cause the gallery to jump back to the first
          // slide after a quick swipe from the last slide.
          touchReleaseOnEdges: this.mql.matches,
          // 1:1 finger tracking on mobile — 1.2x made swipes outrun the finger.
          touchRatio: this.mql.matches ? 1.2 : 1,
          nested: false,
          navigation: {
            nextEl: this.elements.viewer.querySelector('.slider-button--next'),
            prevEl: this.elements.viewer.querySelector('.slider-button--prev'),
          },
          thumbs: this.thumbSwiper ? { swiper: this.thumbSwiper } : undefined,
          on: {
            slideChange: () => {
              const slides = this.elements.viewer.querySelectorAll('.swiper-slide');
              const activeSlide = slides[this.mainSwiper.activeIndex];
              if (activeSlide) {
                this.playActiveMedia(activeSlide);
                // Re-sync the play/pause window as the active slide changes —
                // keeps video decode limited to the active slide + neighbors.
                this.playAllSlideVideos();
                if (this.elements.thumbnails && this.thumbSwiper) {
                  const mediaId = activeSlide.dataset.mediaId;
                  this.updateActiveThumbnail(mediaId);
                  const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
                  if (activeThumbnail) {
                      this.announceLiveRegion(activeSlide, activeThumbnail.dataset.mediaPosition);
                  }
                }
              }
              // Hide hint when reaching last slide
              if (this.mainSwiper.activeIndex >= slides.length - 1) {
                const hint = document.querySelector('.scroll-for-more-hint');
                if (hint) hint.style.display = 'none';
              }
              // Thumbs module disables touch on thumb swiper — re-enable after each slide change
              if (this.thumbSwiper) {
                this.thumbSwiper.allowTouchMove = true;
              }
              // Move sliding pagination indicator
              this.updatePaginationIndicator();
            },
            init: () => {
              setTimeout(() => this.updatePaginationIndicator(), 50);
            },
            progress: () => {
              this.updatePaginationIndicator();
            },
            setTransition: (_swiper, duration) => {
              const pagination = this.elements.pagination;
              if (!pagination) return;
              if (duration > 0) {
                pagination.style.setProperty('--indicator-transition', `${duration}ms`);
                pagination.classList.add('indicator-animating');
              } else {
                pagination.classList.remove('indicator-animating');
              }
            },
          }
        });

        // Guarantee every real Swiper-driven jump plays at a MINIMUM visible pace,
        // scaled by how much distance it actually covers — not just a flat minimum
        // duration. Two separate Swiper code paths can otherwise skip straight from
        // wherever you are to the first/last image with nothing in between ever
        // rendered:
        //  1) freeMode momentum's boundary-hit duration is literally
        //     remainingDistance / velocity (real physics) — a hard flick's inflated
        //     velocity can collapse this toward 0 regardless of how far it travels.
        //  2) If a fast/long drag pushes the position past the boundary while STILL
        //     TOUCHING (not just on release), Swiper skips the momentum math
        //     entirely and calls slideTo(lastSlide) with a FIXED duration (params.speed)
        //     — a 20-image gallery snaps to the end in the same fixed time as a
        //     2-image one.
        // Both paths (and everything else Swiper animates) funnel through these same
        // two primitives: setTransition(duration) followed by setTranslate(target).
        // Track the duration setTransition was asked for, then when setTranslate
        // reveals the actual distance being covered, retroactively stretch the
        // transition if it would average faster than MAX_PX_PER_MS.
        const MIN_SWIPE_ANIMATION_MS = 500;
        const MAX_PX_PER_MS = 1.6; // lower = slower forced minimum pace for long jumps
        let _pendingSwipeDuration = 0;
        const _origSetTransition = this.mainSwiper.setTransition.bind(this.mainSwiper);
        const _origSetTranslate = this.mainSwiper.setTranslate.bind(this.mainSwiper);
        this.mainSwiper.setTransition = (duration) => {
          _pendingSwipeDuration = duration;
          return _origSetTransition(duration);
        };
        this.mainSwiper.setTranslate = (newTranslate) => {
          // duration===0 is intentionally left alone — that's the live 1:1
          // finger-follow transform during an active drag and the frame-lock pin
          // used during page-scroll animations, both of which must stay instant.
          if (_pendingSwipeDuration > 0) {
            const distance = Math.abs(newTranslate - this.mainSwiper.translate);
            const requiredDuration = Math.max(MIN_SWIPE_ANIMATION_MS, distance / MAX_PX_PER_MS);
            if (_pendingSwipeDuration < requiredDuration) {
              _origSetTransition(requiredDuration);
            }
          }
          _pendingSwipeDuration = 0;
          return _origSetTranslate(newTranslate);
        };

        // Thumbs module sets allowTouchMove=false on thumb swiper — re-enable so user can scroll thumbnails
        if (this.thumbSwiper) {
          this.thumbSwiper.allowTouchMove = true;
        }

        // Swiper's native mousewheel module now drives normal gallery scroll,
        // but its own releaseOnEdges doesn't reliably let go once the gallery
        // is visually at its last frame — the wheel gesture stays captive
        // with no page scroll. The listener below bypasses that: it measures
        // the real DOM position (last slide's bottom vs the viewer's own
        // clipped bottom edge — NOT window.innerHeight, which is offset from
        // the viewer by the sticky top gap and header height) and drives
        // window scroll directly once there is no more gallery left to
        // reveal, taking over from Swiper for just that moment so the two
        // don't fight over the event. Shared by the wheel and touch hand-off
        // below.
        const getFirstSlide = () => {
          const slides = this.elements.viewer.querySelectorAll('.swiper-slide');
          return slides[0] || null;
        };
        const getLastSlide = () => {
          const slides = this.elements.viewer.querySelectorAll('.swiper-slide');
          return slides[slides.length - 1] || null;
        };
        const isAtGalleryVisualEnd = () => {
          const lastSlide = getLastSlide();
          if (!lastSlide) return false;
          const viewerRect = this.elements.viewer.getBoundingClientRect();
          return lastSlide.getBoundingClientRect().bottom <= viewerRect.bottom + 2;
        };
        const isAtGalleryVisualStart = () => {
          const firstSlide = getFirstSlide();
          if (!firstSlide) return false;
          const viewerRect = this.elements.viewer.getBoundingClientRect();
          return firstSlide.getBoundingClientRect().top >= viewerRect.top - 2;
        };

        const hasOwnScrollableAncestor = (target) => {
          let el = target instanceof Element ? target : target?.parentElement;
          while (el && el !== document.documentElement) {
            const style = getComputedStyle(el);
            const scrollsY = (style.overflowY === 'auto' || style.overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 1;
            if (scrollsY) return true;
            el = el.parentElement;
          }
          return false;
        };

        // This listener is now the ONLY thing that drives gallery scroll on
        // wheel/trackpad — Swiper's own mousewheel module is disabled (see
        // `mousewheel: false` above) rather than shared with it. The two
        // previously fought over the same gesture at the edges: this file's
        // own hand-off would release the wheel to Swiper expecting Swiper's
        // `releaseOnEdges` to take back over, but that module's release/
        // re-arm state is private and only re-arms on its own mouseenter —
        // so a gesture that changes direction (scroll down to the gallery's
        // end, hand off to the page, then scroll back up with the cursor
        // never leaving the gallery) could land on a tick where this file
        // handed off to Swiper and Swiper was still released: nobody moved
        // the gallery until a real mousemove fired a fresh mouseenter. With
        // Swiper's module out of the picture there is exactly one piece of
        // state machinery (below) and exactly one thing driving the
        // gallery, so there's nothing left to fall out of sync.
        //
        // Gallery movement itself is done by writing translate directly
        // (mimicking what Swiper's freeMode+mousewheel combo did), clamped
        // to the first/last slide's edges. Once a wheel tick would clamp
        // past an edge, the excess hands off to window.scrollBy so the page
        // keeps moving instead of the gesture just going dead at the wall.
        const DEBUG_WHEEL = false;
        const getMinTranslate = () => (this.mainSwiper ? this.mainSwiper.minTranslate() : 0);
        const getMaxTranslate = () => (this.mainSwiper ? this.mainSwiper.maxTranslate() : 0);
        const setGalleryTranslate = (translate) => {
          if (!this.mainSwiper) return;
          this.mainSwiper.setTransition(0);
          this.mainSwiper.setTranslate(translate);
          this.mainSwiper.updateProgress(translate);
          this.mainSwiper.updateActiveIndex();
          this.mainSwiper.updateSlidesClasses();
        };

        window.addEventListener('wheel', (e) => {
          const coveredByOverlay = document.body.classList.contains('overflow-hidden');
          if (coveredByOverlay) return;

          const direction = e.deltaY > 0 ? 1 : e.deltaY < 0 ? -1 : 0;
          if (direction === 0) return;

          const viewerRect = this.elements.viewer.getBoundingClientRect();
          const overGallery = e.clientX >= viewerRect.left && e.clientX <= viewerRect.right
            && e.clientY >= viewerRect.top && e.clientY <= viewerRect.bottom;

          if (!overGallery) {
            if (hasOwnScrollableAncestor(e.target)) {
              if (DEBUG_WHEEL) console.debug('[gallery-wheel]', 'skip: not over gallery, other scrollable owns it');
              return;
            }
            const NEAR_GALLERY_PX = 400;
            const distanceToGallery = direction > 0
              ? viewerRect.top - e.clientY
              : e.clientY - viewerRect.bottom;
            if (distanceToGallery > NEAR_GALLERY_PX) {
              if (DEBUG_WHEEL) console.debug('[gallery-wheel]', 'far from gallery: leaving native eased scroll alone', { distanceToGallery });
              return;
            }
            if (direction > 0) {
              const atPageBottom = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 1;
              if (atPageBottom) return;
            } else if (window.scrollY <= 0) {
              return;
            }
            if (DEBUG_WHEEL) console.debug('[gallery-wheel]', 'near gallery: driving page scroll directly', { deltaY: e.deltaY, distanceToGallery });
            e.preventDefault();
            window.scrollBy({ top: e.deltaY, behavior: 'auto' });
            return;
          }

          if (!this.mainSwiper) return;

          e.preventDefault();
          e.stopPropagation();

          const current = this.mainSwiper.translate;
          // Swiper's vertical translate is negative going "forward" through
          // slides — deltaY > 0 (scroll down / forward) must DECREASE
          // translate, matching how Swiper's own freeMode drag maps finger
          // movement to translate.
          const proposed = current - e.deltaY;
          const min = getMinTranslate(); // translate at the first slide (top)
          const max = getMaxTranslate(); // translate at the last slide (bottom), <= min
          const clamped = Math.min(min, Math.max(max, proposed));
          const overflow = proposed - clamped;

          if (clamped !== current) {
            setGalleryTranslate(clamped);
          }

          if (overflow === 0) return;

          // Wheel tick pushed past an edge — hand the leftover motion to the
          // page instead of swallowing it. `overflow` is in the same
          // (inverted) space as translate, so flip sign back to a page
          // scroll delta.
          if (direction > 0) {
            const atPageBottom = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 1;
            if (atPageBottom) return;
          } else if (window.scrollY <= 0) {
            return;
          }
          if (DEBUG_WHEEL) console.debug('[gallery-wheel]', 'HANDING OFF: window.scrollBy', { overflow });
          window.scrollBy({ top: -overflow, behavior: 'auto' });
        }, { passive: false, capture: true });

        // Touch equivalent of the wheel hand-off above, but to the mobile parallax
        // panel specifically rather than plain page scroll: on mobile the gallery sits
        // in a position:sticky wrapper, not normal scroll flow, so there's no page
        // scroll to hand off to here — swiping up once already at the last slide
        // (nowhere further for the gallery itself to go) should instead expand the
        // info panel. Purely passive observation: never calls preventDefault, so
        // Swiper's own touch handling (including its edge resistance/rubber-band) runs
        // completely undisturbed — normal swiping between images is untouched.
        const SWIPE_UP_EXPAND_THRESHOLD_PX = 40;
        let galleryTouchStartY = null;
        let galleryTouchStartedAtEnd = false;
        this.elements.viewer.addEventListener('touchstart', (e) => {
          galleryTouchStartY = e.touches[0] ? e.touches[0].clientY : null;
          galleryTouchStartedAtEnd = isAtGalleryVisualEnd();
        }, { passive: true });
        this.elements.viewer.addEventListener('touchend', (e) => {
          const startY = galleryTouchStartY;
          const startedAtEnd = galleryTouchStartedAtEnd;
          galleryTouchStartY = null;
          if (startY === null || !startedAtEnd) return;
          if (this.mainSwiper && this.mainSwiper.animating) return;
          if (!isAtGalleryVisualEnd()) return;
          const touch = e.changedTouches && e.changedTouches[0];
          if (!touch) return;
          const deltaY = startY - touch.clientY; // positive = swiped up
          if (deltaY < SWIPE_UP_EXPAND_THRESHOLD_PX) return;
          if (typeof window.__pdpExpandPanel === 'function') window.__pdpExpandPanel();
        }, { passive: true });

        // Reverse of the hand-off above: consumed by product-parallax-mobile.js when
        // the info panel is swiped back down to fully collapsed (nowhere further for
        // the panel itself to go) — continues that same bottom-to-top gesture by
        // stepping the gallery back one slide, so scrolling up works the same
        // continuous way scrolling down does, without the user re-touching the
        // gallery itself.
        window.__pdpGallerySlidePrev = () => {
          if (!this.mainSwiper || this.mainSwiper.isBeginning) return false;
          this.mainSwiper.slidePrev();
          return true;
        };

// observer/resizeObserver are off on mainSwiper (see the config comment above) —
        // a real viewport resize/orientation change still needs Swiper to recompute its
        // translate bounds, just not on every unrelated DOM mutation. Debounced so the
        // continuous stream of resize events during an iOS toolbar show/hide or a drag
        // of the browser window edge doesn't call update() on every single tick.
        let resizeUpdateTimer = null;
        const scheduleSwiperUpdate = () => {
          clearTimeout(resizeUpdateTimer);
          resizeUpdateTimer = setTimeout(() => {
            if (this.mainSwiper) this.mainSwiper.update();
            if (this.thumbSwiper) this.thumbSwiper.update();
          }, 150);
        };
        window.addEventListener('resize', scheduleSwiperUpdate, { passive: true });
        window.addEventListener('orientationchange', scheduleSwiperUpdate, { passive: true });

        // Sync initial active thumbnail (in case thumbs module doesn't set aria-current)
        const initialSlide = this.elements.viewer?.querySelectorAll('.swiper-slide')?.[this.mainSwiper.activeIndex];
        if (initialSlide?.dataset?.mediaId) this.updateActiveThumbnail(initialSlide.dataset.mediaId);

        // Autoplay all slide videos on init
        this.playAllSlideVideos();

        // Bind click-to-pause on all video wrappers
        this.initVideoClickToPause();

        // Show scroll-for-more hint once on first PDP visit — after page fully loaded
        if (document.readyState === 'complete') {
          this.initScrollForMoreHint();
        } else {
          window.addEventListener('load', () => this.initScrollForMoreHint(), { once: true });
        }
      }

      getActiveMediaId() {
        const slides = this.elements.viewer.querySelectorAll('.swiper-slide');
        const index = this.mainSwiper ? this.mainSwiper.activeIndex : this._activeIndex;
        if (index == null) return null;
        return slides[index]?.dataset?.mediaId || null;
      }

      // Scrolls the given slide index into view — native-scroll equivalent of
      // Swiper's slideTo(), used by setActiveMedia/setActiveMediaByImageUrl/
      // __pdpGallerySlidePrev when there's no Swiper instance to ask.
      _scrollViewerToIndex(index, instant = false) {
        const viewer = this.elements.viewer;
        const slides = viewer.querySelectorAll('.swiper-slide');
        const slide = slides[index];
        if (!slide) return;
        this._activeIndex = index;
        // slide.offsetTop is relative to its offsetParent, which is the swiper-wrapper
        // div (vendor Swiper CSS gives it position:relative unconditionally) — NOT
        // necessarily flush with viewer's own scroll frame. Compare live rendered
        // rects instead so this lands correctly regardless of that offsetParent chain.
        const targetTop = viewer.scrollTop + (slide.getBoundingClientRect().top - viewer.getBoundingClientRect().top);
        viewer.scrollTo({ top: targetTop, behavior: instant ? 'auto' : 'smooth' });
      }

      // `instant` skips the slide/scroll animation entirely — used when the gallery
      // is being synced to reflect a choice made elsewhere (e.g. the zoom modal's own
      // thumbnail strip) rather than a direct interaction with this gallery, where a
      // visible scroll/slide playing out after the fact reads as the gallery catching
      // up rather than already being on the right image.
      setActiveMedia(mediaId, instant = false) {
        const slides = Array.from(this.elements.viewer.querySelectorAll('.swiper-slide'));
        const activeIndex = slides.findIndex(s => s.dataset.mediaId === mediaId);
        if (activeIndex === -1) return;
        if (this.mainSwiper) {
          // Called right after product-info.js reorders/adds/removes <li> slides for a
          // variant swap. observer/resizeObserver are off on this swiper (see the config
          // comment in initSwiper), so Swiper needs telling explicitly that slide count/
          // order just changed before it can slideTo() a now-possibly-different index.
          this.mainSwiper.update();
          this.mainSwiper.slideTo(activeIndex, instant ? 0 : 350);
        } else {
          this._scrollViewerToIndex(activeIndex, instant);
        }
      }

      setActiveMediaByImageUrl(imageUrl) {
        if (!imageUrl) return false;

        const normalizedTarget = this._normalizeMediaUrl(imageUrl);
        if (!normalizedTarget) return false;

        const slides = Array.from(this.elements.viewer.querySelectorAll('.swiper-slide'));
        const activeIndex = slides.findIndex((slide) => {
          const image = slide.querySelector('.product__modal-opener--image img, .product__media img');
          if (!image) return false;

          const candidates = [
            image.currentSrc,
            image.getAttribute('src'),
            image.getAttribute('data-src'),
            image.getAttribute('srcset')?.split(',')[0]?.trim()?.split(' ')[0],
          ];

          return candidates.some((candidate) => {
            return this._normalizeMediaUrl(candidate) === normalizedTarget;
          });
        });

        if (activeIndex === -1) return false;

        if (this.mainSwiper) {
          this.mainSwiper.slideTo(activeIndex, 350);
        } else {
          this._scrollViewerToIndex(activeIndex);
        }
        return true;
      }

      _normalizeMediaUrl(url) {
        if (!url) return '';

        try {
          const parsed = new URL(url, window.location.origin);
          return parsed.pathname.replace(/_(\d+x|\d+x\d+)(?=\.[a-z]+$)/i, '');
        } catch (error) {
          return String(url).split('?')[0].replace(/_(\d+x|\d+x\d+)(?=\.[a-z]+$)/i, '');
        }
      }

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion) return;
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);

        // Play video on the given slide — playAllSlideVideos() keeps only this
        // slide plus its immediate neighbors decoding at once (see below).
        activeItem.querySelectorAll('video').forEach((video) => {
          try {
            video.muted = true;
            video.loop = true;
            video.playsInline = true;
            video.setAttribute('playsinline', '');
            video.setAttribute('muted', '');
            video.setAttribute('loop', '');
            video.removeAttribute('controls');
            // Only play if not manually paused by user tap
            if (!video._userPaused) {
              const p = video.play();
              if (p && typeof p.catch === 'function') p.catch(() => {});
            }
          } catch (e) {}
        });

        // Clear paused state so play icon hides when returning to this slide
        activeItem.querySelectorAll('.product__media--video-wrapper').forEach((w) => {
          if (!w.querySelector('video')?._userPaused) w.classList.remove('is-paused');
        });
      }

      playAllSlideVideos() {
        // Scope to the main viewer only — thumbnail items also carry the
        // .swiper-slide class (Swiper requires it), so an unscoped query here
        // would autoplay thumbnail videos too, which must always stay paused.
        if (!this.elements.viewer) return;
        // Only the active slide + its immediate neighbors stay decoding/playing.
        // Every gallery video autoplaying at once (regardless of distance from
        // the active slide) competes with the swipe gesture for decode
        // bandwidth on-device — a real source of dropped frames while scrolling.
        // Neighbors (not just the active slide) are kept warm so there's no
        // play() startup lag the instant a video slide is swiped into view.
        const activeIndex = this.mainSwiper ? this.mainSwiper.activeIndex : (this._activeIndex || 0);
        this.elements.viewer.querySelectorAll('.swiper-slide').forEach((slide, index) => {
          const inWindow = Math.abs(index - activeIndex) <= 1;
          slide.querySelectorAll('video').forEach((video) => {
            if (!inWindow) {
              if (!video.paused) video.pause();
              return;
            }
            try {
              video.muted = true;
              video.loop = true;
              video.playsInline = true;
              video.setAttribute('playsinline', '');
              video.setAttribute('muted', '');
              video.setAttribute('loop', '');
              video.removeAttribute('controls');
              if (video._userPaused) return;
              if (video.readyState >= 2) {
                const p = video.play();
                if (p && typeof p.catch === 'function') p.catch(() => {});
              } else {
                const tryPlay = () => {
                  if (!video._userPaused) {
                    const p = video.play();
                    if (p && typeof p.catch === 'function') p.catch(() => {});
                  }
                };
                video.addEventListener('loadeddata', tryPlay, { once: true });
                video.addEventListener('canplay', tryPlay, { once: true });
                video.load();
              }
            } catch (e) {}
          });
        });
      }

      updatePaginationIndicator() {
        const pagination = this.elements.pagination;
        if (!pagination) return;
        const bullets = pagination.querySelectorAll('.swiper-pagination-bullet');
        if (!bullets.length) return;
        const total = bullets.length;
        const paginationHeight = pagination.offsetHeight;
        const indicatorHeight = paginationHeight / total;
        const progress = this.mainSwiper ? (this.mainSwiper.progress || 0) : (this._nativeProgress || 0);
        const maxOffset = paginationHeight - indicatorHeight;
        const offsetY = progress * maxOffset;
        pagination.style.setProperty('--indicator-y', `${offsetY}px`);
        pagination.style.setProperty('--indicator-h', `${indicatorHeight}px`);
      }

      updateActiveThumbnail(mediaId) {
        if (!this.elements.thumbnails) return;
        const all = this.elements.thumbnails.querySelectorAll('[data-target] .thumbnail');
        all.forEach((btn) => {
          btn.removeAttribute('aria-current');
          btn.classList.remove('is-active');
        });

        const item = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        const btn = item?.querySelector('.thumbnail');
        if (!btn) return;
        btn.setAttribute('aria-current', 'true');
        btn.classList.add('is-active');

        // Ensure the active thumb stays visible — skip during hover preview so the strip doesn't jump under the cursor
        if (!this._suppressThumbSync) {
          if (this.thumbSwiper) {
            const slides = Array.from(this.elements.thumbnails.querySelectorAll('.swiper-slide'));
            const idx = slides.indexOf(item);
            if (idx >= 0) {
              this.thumbSwiper.slideTo(idx, 200);
              // Thumbs module may reset allowTouchMove — force it back on
              this.thumbSwiper.allowTouchMove = true;
            }
          } else {
            const container = this.elements.thumbnails;
            // getBoundingClientRect, not offsetTop — item.offsetParent is the
            // thumbnail-list wrapper (vendor Swiper CSS gives it position:relative
            // unconditionally), not necessarily flush with container's scroll frame.
            const itemTop = container.scrollTop + (item.getBoundingClientRect().top - container.getBoundingClientRect().top);
            container.scrollTo({
              top: itemTop - (container.clientHeight - item.offsetHeight) / 2,
              behavior: 'smooth',
            });
          }
        }
        this._suppressThumbSync = false;
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer) return;
        const wrapper = this.elements.viewer.querySelector('.swiper-wrapper');
        if (wrapper) wrapper.setAttribute('role', 'presentation');
        this.elements.viewer.querySelectorAll('.swiper-slide').forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      initVideoClickToPause() {
        this.querySelectorAll('.product__media--video-wrapper').forEach((wrapper) => {
          if (wrapper._videoClickBound) return;
          wrapper._videoClickBound = true;

          wrapper.addEventListener('click', (e) => {
            const video = wrapper.querySelector('video');
            if (!video) return;
            // Play button clicked — resume and clear user-paused flag
            if (e.target.closest('.product__video-play-btn')) {
              video._userPaused = false;
              video.play().catch(() => {});
              wrapper.classList.remove('is-paused');
              this.syncThumbnailVideo(wrapper.dataset.mediaId);
              return;
            }
            // Wrapper tap — toggle; track user intent so autoplay doesn't override
            if (video.paused) {
              video._userPaused = false;
              video.play().catch(() => {});
              wrapper.classList.remove('is-paused');
              this.syncThumbnailVideo(wrapper.dataset.mediaId);
            } else {
              video._userPaused = true;
              video.pause();
              wrapper.classList.add('is-paused');
              this.syncThumbnailVideo(wrapper.dataset.mediaId);
            }
          });
        });
      }

      // Thumbnail videos must always stay paused, regardless of the main video's state.
      syncThumbnailVideo(mediaId) {
        if (!this.elements.thumbnails || !mediaId) return;
        const cell = this.elements.thumbnails.querySelector(`.thumbnail__group-cell[data-media-id="${mediaId}"]`);
        const video = cell?.querySelector('video');
        if (!video) return;
        video.pause();
      }

      initScrollForMoreHint() {
        if (window.innerWidth > 1023) return;
        const hint = document.querySelector('.scroll-for-more-hint');
        if (!hint) return;

        const cacheDays = Math.max(1, parseInt(hint.dataset.cacheDays, 10) || 1);
        const cacheKey = 'nude_pdp_scroll_hint_shown';
        const stored = +localStorage.getItem(cacheKey) || 0;
        if (Date.now() - stored < cacheDays * 86400000) {
          hint.style.display = 'none';
          return;
        }

        localStorage.setItem(cacheKey, Date.now());

        // `this` is the <media-gallery> element
        const galleryEl = this;
        let peekDone = false;

        const endPeek = () => {
          if (peekDone) return;
          peekDone = true;
          galleryEl.classList.add('peek-return');
          galleryEl.classList.remove('peek-animate');
          // Matches the 0.6s transform transition on both media-gallery and
          // .scroll-for-more-hint in section-main-product.css — cutting this short
          // (the old 550ms vs. a since-lengthened 0.6s transition) chopped the last
          // bit of the closing motion off instead of letting it finish.
          setTimeout(() => {
            galleryEl.classList.remove('peek-return');
            hint.style.display = 'none';
          }, 600);
        };

        // Short delay after load for layout to paint
        setTimeout(() => {
          // hint.style.visibility = 'visible';
          // Double rAF: ensure browser paints initial state before adding transition class
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              galleryEl.classList.add('peek-animate');
            });
          });

          // Hold 1.8s then slide back down
          this._peekHoldTimer = setTimeout(endPeek, 1800);

          // Dismiss on user interaction
          const onUserInput = () => {
            clearTimeout(this._peekHoldTimer);
            endPeek();
          };
          window.addEventListener('touchstart', onUserInput, { passive: true, once: true });
          window.addEventListener('wheel', onUserInput, { passive: true, once: true });
          window.addEventListener('scroll', onUserInput, { passive: true, once: true });
        }, 300);
      }

      // Thumbnail videos are previews only — they must never autoplay, even
      // though the corresponding main-viewer video does. Always keep them paused.
      playThumbnailVideos() {
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('video').forEach((video) => {
          video.muted = true;
          video.loop = true;
          video.playsInline = true;
          video.setAttribute('muted', '');
          video.setAttribute('loop', '');
          video.setAttribute('playsinline', '');
          video.removeAttribute('autoplay');
          video.removeAttribute('controls');
          video.pause();
        });
      }
    }
  );
}
